import { ROUTES } from '#/config/routes';
import { isAuthEnabled } from '#/config/auth';
import { logout } from '#/modules/infra/redux/infraSlice';
import { clearAccess } from '#/utils/auth/tokenStorage';
import { toast } from '#/utils/toast';
import { SESSION_EXIT_MESSAGES } from '#/utils/toast/presets';

export const SESSION_EXIT_REASON = {
  LOGOUT_SUCCESS: 'logout_success',
  PASSWORD_CHANGED: 'password_changed',
  TOKEN_EXPIRED: 'token_expired',
  TOKEN_INVALID: 'token_invalid',
  TOKEN_REQUIRED: 'token_required',
};

const SESSION_EXIT_TOAST_ID = 'session-exit';
const SESSION_EXIT_TOAST_REASON_KEY = 'session_exit_toast_reason';

let sessionExitInProgress = false;
let injectedDispatch = null;
let injectedNavigate = null;

export function setSessionExitDispatch(dispatch) {
  injectedDispatch = dispatch;
}

export function setSessionExitNavigate(navigateFn) {
  injectedNavigate = navigateFn;
}

export function getSessionExitMessage(reason) {
  return SESSION_EXIT_MESSAGES[reason] ?? SESSION_EXIT_MESSAGES.token_invalid;
}

export function mapAuthCodeToSessionExitReason(code) {
  switch (code) {
    case 'AUTH_TOKEN_EXPIRED':
      return SESSION_EXIT_REASON.TOKEN_EXPIRED;
    case 'AUTH_TOKEN_INVALID':
      return SESSION_EXIT_REASON.TOKEN_INVALID;
    case 'AUTH_TOKEN_REQUIRED':
      return SESSION_EXIT_REASON.TOKEN_REQUIRED;
    case 'AUTH_UNAUTHORIZED':
      return SESSION_EXIT_REASON.TOKEN_INVALID;
    default:
      return SESSION_EXIT_REASON.TOKEN_INVALID;
  }
}

/**
 * 登入 API 401（帳密錯誤）不觸發 session exit。
 */
export function shouldSkipSessionExit(error, config) {
  if (config?.skipAuth || config?.skipSessionExit) {
    return true;
  }

  const requestUrl = config?.url || '';
  if (requestUrl.includes('/auth/logout')) {
    return true;
  }

  if (typeof window !== 'undefined' && window.location.pathname === ROUTES.login) {
    return true;
  }

  const code = error.response?.data?.code;
  if (code === 'AUTH_INVALID_CREDENTIALS') {
    return true;
  }

  return false;
}

function resolveDispatch(dispatch) {
  return dispatch || injectedDispatch;
}

function navigateToLogin() {
  if (injectedNavigate) {
    injectedNavigate(ROUTES.login, { replace: true });
    return;
  }

  if (typeof window !== 'undefined') {
    window.history.replaceState(null, '', ROUTES.login);
  }
}

function persistSessionExitToastReason(reason) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(SESSION_EXIT_TOAST_REASON_KEY, reason);
  } catch {
    // sessionStorage 不可用時仍嘗試即時 toast
  }
}

function showSessionExitToast(reason) {
  toast.session(reason, { toastId: SESSION_EXIT_TOAST_ID });
}

/**
 * LoginPage mount：消費 exitToLogin 寫入的 pending reason 並 toast。
 * 冷啟動無 pending → no-op。
 */
export function consumePendingSessionExitToast() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const reason = window.sessionStorage.getItem(SESSION_EXIT_TOAST_REASON_KEY);
    if (!reason) {
      return;
    }
    window.sessionStorage.removeItem(SESSION_EXIT_TOAST_REASON_KEY);
    showSessionExitToast(reason);
  } catch {
    // sessionStorage 不可用 → no-op
  }
}

/**
 * 統一 session 結束（方案 B）：清 token、Redux logout、toast、立刻導向登入頁。
 * reason 寫入 sessionStorage，供 LoginPage mount 補顯示（導向後 Toast 可能被卸載）。
 * @param {object} [opts]
 * @param {string} [opts.reason]
 * @param {Function} [opts.dispatch]
 * @param {boolean} [opts.skipToast] — true：仍清 token／logout／導 login，不 toast、不寫 pending
 */
export function exitToLogin({
  reason = SESSION_EXIT_REASON.TOKEN_INVALID,
  dispatch,
  skipToast = false,
} = {}) {
  if (!isAuthEnabled) {
    return;
  }

  if (sessionExitInProgress) {
    return;
  }

  sessionExitInProgress = true;

  clearAccess();

  const resolvedDispatch = resolveDispatch(dispatch);
  if (resolvedDispatch) {
    resolvedDispatch(logout());
  }

  if (!skipToast) {
    persistSessionExitToastReason(reason);
    showSessionExitToast(reason);
  }
  navigateToLogin();
}

/** 登入成功／測試用：重置防重複旗標，讓下次過期仍可 exit */
export function resetSessionExitFlag() {
  sessionExitInProgress = false;
}
